/** Browser file download helpers with optional progress (XHR). */

export type DownloadProgress = {
    loaded: number;
    total: number | null;
    /** 0–100 when Content-Length is known; otherwise null */
    percent: number | null;
};

export function downloadBlobFromUrl(url: string, onProgress?: (progress: DownloadProgress) => void): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.onprogress = event => {
            if (!onProgress) return;
            const total = event.lengthComputable ? event.total : null;
            onProgress({
                loaded: event.loaded,
                total,
                percent: total && total > 0 ? Math.min(100, Math.round((event.loaded / total) * 100)) : null,
            });
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                onProgress?.({
                    loaded: xhr.response?.size ?? 0,
                    total: xhr.response?.size ?? null,
                    percent: 100,
                });
                resolve(xhr.response as Blob);
                return;
            }
            reject(new Error(`Download failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error('Download failed'));
        xhr.onabort = () => reject(new Error('Download cancelled'));
        xhr.send();
    });
}

export function saveBlob(blob: Blob, filename: string) {
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
}

/** Fetch + trigger the browser save dialog. */
export async function downloadUrlAsFile(
    url: string,
    filename: string,
    onProgress?: (progress: DownloadProgress) => void,
): Promise<Blob> {
    const blob = await downloadBlobFromUrl(url, onProgress);
    saveBlob(blob, filename);
    return blob;
}

/** Response-shaped object so existing `.blob()` callers keep working. */
export function blobAsFetchResponse(blob: Blob): { ok: true; status: number; blob: () => Promise<Blob> } {
    return {
        ok: true,
        status: 200,
        blob: async () => blob,
    };
}
